<script setup>
import { Comment, computed, Text, useSlots } from 'vue';

import { HrCard, HrIcon } from '../primitives';

/**
 * Titled content surface — a card with a labeled header, scrollable body, and optional footer actions.
 *
 * Use it wherever a named section groups related content or form fields on a page.
 * Replaces the common `q-card flat` + `q-card-section` + heading pattern throughout the app.
 *
 * @status recommended
 * @group cards-dialogs
 * @useWhen Grouping related content or form fields under a named section on a page
 * @useWhen Wrapping a form section that has its own Save / Cancel actions
 * @avoidWhen Content needs to expand/collapse — use HrExpandableSection
 * @avoidWhen Content is tabular — use HrDataTable
 * @related HrExpandableSection
 * @related HrFormDialog
 */
defineOptions({ name: 'HrContentCard' });

const props = defineProps({
  /**
   * Section heading displayed in the card header row.
   * When null and no `header-actions` slot content is provided, the header row is suppressed entirely.
   */
  title: {
    type: String,
    default: null,
  },
  /**
   * Leading icon name (Material Symbols) shown in the header row alongside the title.
   * Only rendered when `title` is set.
   */
  icon: {
    type: String,
    default: null,
  },
});

const slots = useSlots();

const slotIsEmpty = (slot) => {
  if (!slot) {
    return true;
  }
  return slot().every((vnode) => {
    if (vnode.type === Comment) {
      return true;
    }
    return vnode.type === Text && String(vnode.children).trim() === '';
  });
};

const hasHeader = computed(() => !!props.title || !slotIsEmpty(slots['header-actions']));
const hasActions = computed(() => !slotIsEmpty(slots.actions));
</script>

<template>
  <hr-card
    variant="elevated"
    shape="xl"
    :legacy-props="{ flat: true, bordered: true }"
  >
    <!-- @todo Remove legacy props when Legacy theme is removed.
      Keep comments inside the root: production builds keep them, and one beside the root makes
      this a fragment that drops the caller's class and attrs. -->
    <div
      v-if="hasHeader"
      class="hr-content-card__header"
    >
      <div class="hr-content-card__heading">
        <hr-icon
          v-if="icon && title"
          :name="icon"
          size="sm"
          class="hr-content-card__icon"
        />
        <span v-if="title">{{ title }}</span>
      </div>
      <!-- @slot Trailing content in the header row (icon buttons, badges). -->
      <slot name="header-actions" />
    </div>
    <div :class="['hr-content-card__body', { 'hr-content-card__body--flush-top': hasHeader }]">
      <!-- @slot Card body content. -->
      <slot />
    </div>
    <div
      v-if="hasActions"
      class="hr-content-card__footer"
    >
      <!-- @slot Footer action buttons. Rendered right-aligned. -->
      <slot name="actions" />
    </div>
  </hr-card>
</template>

<style lang="scss" scoped>
.hr-content-card {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
  }

  &__heading {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
    font-size: 1rem;
    line-height: 1.5;
    color: var(--app-on-surface, currentColor);
  }

  &__icon {
    color: var(--app-on-surface-variant, #666);
  }

  &__body {
    padding: 16px;

    &--flush-top {
      padding-top: 0;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 8px 16px;
    border-top: 1px solid var(--app-outline-variant, rgba(0, 0, 0, 0.12));
  }
}
</style>
